import { CURRENT_SCHEME_KEY } from '~/constants/localStorage'
import { createContext, useState, ReactNode } from 'react'
import { SCHEME } from '~/types'
import {
  type FormattedAmount,
  type SchemeConfigFragment,
  useSchemeConfigFragment,
  useSchemeConfigQuery,
} from '~/gql/generated/graphql'

type CurrentSchemeCtx = {
  currentSchemeId: SCHEME['id'] | null
  setCurrentSchemeId: (scheme: SCHEME['id']) => void
  schemeConfig: SchemeConfigFragment
}

// Variant of type with non optional scheme_accounting
type SchemeConfigFragmentWithNonNullableAccounting = {
  scheme_accounting: NonNullable<SchemeConfigFragment['scheme_accounting']>
} & SchemeConfigFragment

// - Get scheme config fragment
// - return data merged with defaults so its not nullable
const defaultCharge: FormattedAmount = {
  __typename: 'FormattedAmount',
  amount: 0,
  formatted_amount: '0',
  unit: 'currency',
  currency_code: 'GBP',
}
const defaultSchemeConfig: SchemeConfigFragmentWithNonNullableAccounting = {
  __typename: 'Scheme',
  id: '',
  config: {
    __typename: 'SchemeConfig',
    is_vehicle_rewards_enabled: false,
    is_parking_photo_enabled: false,
  },
  scheme_accounting: {
    id: 'xxx',
    __typename: 'SchemeAccounting',
    scheme_pricing_configs: {
      __typename: 'SchemePricingConfigs',
      out_of_service_area_parking_charge: defaultCharge,
      out_of_zone_parking_charge: defaultCharge,
      undocked_in_zone_parking_charge: defaultCharge,
      unlocked_vehicle_charge: defaultCharge,
      rear_lock_unlocked_vehicle_charge: defaultCharge,
    },
  },
} as const

export const CurrentSchemeContext = createContext<CurrentSchemeCtx>({
  currentSchemeId: null,
  setCurrentSchemeId: () => {},
  schemeConfig: defaultSchemeConfig,
})

type Props = {
  schemeId?: SCHEME['id']
  children: ReactNode
}

// Merge defaults with query result to return something
// in the expected shape, with no nulls
const mergeDefaultSchemeConfig = (
  defaultSchemeConfig: SchemeConfigFragmentWithNonNullableAccounting,
  data?: SchemeConfigFragment,
) => {
  const dataWithDefaults = {
    ...defaultSchemeConfig,
    ...data,
  }
  const { scheme_pricing_configs } =
    data?.scheme_accounting ?? defaultSchemeConfig.scheme_accounting

  return {
    ...dataWithDefaults,
    // Merge this in up a level so consumers dont have to drill through scheme_accounting
    scheme_pricing_configs,
  }
}

// Get scheme config from Apollo cache, merged with defaults to make it non nullable
// Use this if you've got a query that includes the SchemeConfig fragment for the scheme
// ID in question. If not, just access it automatically for the current scheme in the
// context below
export const useCachedSchemeConfigWithDefault = (schemeId: string) => {
  const { data, complete } = useSchemeConfigFragment({ id: schemeId })
  return mergeDefaultSchemeConfig(
    defaultSchemeConfig,
    complete ? data : defaultSchemeConfig,
  )
}

export default ({ schemeId, children }: Props) => {
  const [currentSchemeId, setCurrentSchemeId] = useState(
    schemeId || window.localStorage.getItem(CURRENT_SCHEME_KEY),
  )

  function persistSchemeId(id: SCHEME['id']) {
    setCurrentSchemeId(id)
    window.localStorage.setItem(CURRENT_SCHEME_KEY, id)
  }

  // Get scheme confif for current scheme
  const { data: schemeConfigData } = useSchemeConfigQuery(
    currentSchemeId
      ? { variables: { schemeId: currentSchemeId }, pollInterval: 0 }
      : { skip: true },
  )

  // Merge with defaults to ensure we can always return something.
  // There should always be a scheme_accounting, so this is just to be type safe
  const schemeConfig = mergeDefaultSchemeConfig(
    defaultSchemeConfig,
    schemeConfigData?.scheme ?? undefined,
  )

  return (
    <CurrentSchemeContext.Provider
      value={{
        currentSchemeId,
        setCurrentSchemeId: persistSchemeId,
        schemeConfig,
      }}
    >
      {children}
    </CurrentSchemeContext.Provider>
  )
}
